/**
 * useFinanceiroCobrancasPaginated - Hook de paginação server-side para cobranças
 * 
 * Utiliza RPC get_cobrancas_paginated com offset-based pagination
 * Replica o padrão de useCrmCustomersPaginated para consistência
 * 
 * @version 1.0.0
 * @author Sistema
 * RELEASE: 2026-03-15 v1.0.0 - Criação do hook com filtros avançados e KPIs server-side
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger para depuração
const logger = createLogger('FinanceiroCobrancasPaginated');

/**
 * Interface da cobrança retornada pela RPC get_cobrancas_paginated
 * Estrutura flat otimizada para tabelas (sem embeds aninhados)
 */
export interface PaginatedCobranca {
  // Dados da cobrança
  id: string;
  tenant_id: string;
  customer_id: string;
  deal_id: string | null;
  tipo_cobranca: string;
  meio_pagamento_id: string | null;
  conta_corrente_id: string | null;
  descricao: string | null;
  valor_total: number;
  moeda: string;
  status: string;
  data_emissao: string;
  data_vencimento: string | null;
  observacoes: string | null;
  numero_parcelas: number;
  provider_id: string | null;
  provider_charge_id: string | null;
  provider_slug: string | null;
  provider_invoice_url: string | null;
  provider_payment_link: string | null;
  created_at: string;
  updated_at: string;
  // Campos de JOINs (flat)
  customer_name: string | null;
  // NOVO v1.1.0: Documento do cliente (CPF ou CNPJ)
  customer_document: string | null;
  tipo_pagamento: string | null;
  conta_corrente_nome: string | null;
  primeira_data_pagamento: string | null;
  // v1.6.0: Valor já recebido (soma das parcelas pagas)
  valor_recebido: number;
  // Vínculo com módulo de Recorrência (Fase 6)
  assinatura_id: string | null;
  ciclo_numero: number | null;
  // v1.8.0: Indica se alguma parcela possui comprovante de baixa manual anexado
  has_paid_receipt: boolean;
  // v1.8.0: Origem agregada das parcelas pagas (manual | provider_webhook | mixed | null)
  paid_source: 'manual' | 'provider_webhook' | 'mixed' | null;
  // v2.0.0: Número da fatura do provider (primeira parcela ordenada por numero_parcela)
  numero_fatura: string | null;
  // Contingência de checkout (v2.1.0 RPC - 2026-06-21)
  /** Modo preferido herdado da conta corrente (transparent|provider). */
  checkout_mode: 'transparent' | 'provider' | null;
  /** Slug ativo da sessão de checkout transparente (cache em financeiro_cobrancas). */
  checkout_session_slug: string | null;
  // KPIs globais (mesmo valor em todas as rows)
  kpi_total_faturas: number;
  kpi_total_pagas: number;
  kpi_recebido: number;
  kpi_previsto: number;
  // Paginação (window function)
  total_count: number;
}

/**
 * Interface de filtros para listagem de cobranças
 */
export interface CobrancaPaginatedFilters {
  status?: string | null;
  /**
   * NOVO v1.6.0 (RPC v2.5.0): filtra por vários status ao mesmo tempo.
   * Usado pelos modais de auditoria dos KPIs do Dashboard Financeiro
   * (ex.: 'pendente' + 'aguardando_pagamento').
   */
  status_in?: string[] | null;
  tipo_cobranca?: string | null;
  meio_pagamento_id?: string | null;
  product_id?: string | null;
  // NOVO v1.1.0: Filtro por categoria do produto
  category_id?: string | null;
  search?: string | null;
  data_vencimento_de?: string | null;
  data_vencimento_ate?: string | null;
  data_emissao_de?: string | null;
  data_emissao_ate?: string | null;
  data_pagamento_de?: string | null;
  data_pagamento_ate?: string | null;
  // NOVO v1.3.0: Filtros para abas Financeiro nos modais
  customer_id?: string | null;
  deal_id?: string | null;
  /** NOVO: filtra cobranças originadas de uma tarefa (Retenção/Jurídico/Normal) */
  task_id?: string | null;
  // NOVO v1.4.0: Filtro por provider_payment_link (modal link de pagamento)
  provider_payment_link?: string | null;
  // NOVO v1.5.0 (RPC v1.9.0): novos filtros da listagem
  data_criacao_de?: string | null;
  data_criacao_ate?: string | null;
  conta_corrente_id?: string | null;
  /** Unidade de serviço do produto: 'one_time' | 'recurring' (term_type) */
  product_term_type?: string | null;
  /** NOVO: filtra cobranças vinculadas a um veículo (diretas ou herdadas da assinatura) */
  vehicle_id?: string | null;
}

/**
 * Parâmetros de paginação
 */
interface PaginationParams {
  page: number;
  pageSize: number;
}

/**
 * Interface de KPIs extraídos da RPC
 */
export interface CobrancaKpis {
  totalFaturas: number;
  totalPagas: number;
  recebido: number;
  previsto: number;
}

/**
 * Resultado da query paginada
 */
export interface PaginatedCobrancasResult {
  cobrancas: PaginatedCobranca[];
  totalCount: number;
  totalPages: number;
  kpis: CobrancaKpis;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook para buscar cobranças com paginação server-side
 * Utiliza RPC get_cobrancas_paginated para performance em grande escala
 * 
 * @param filters - Filtros para a busca (status, tipo, datas, etc.)
 * @param pagination - Parâmetros de paginação (page, pageSize)
 */
export function useFinanceiroCobrancasPaginated(
  filters: CobrancaPaginatedFilters | undefined,
  pagination: PaginationParams
): PaginatedCobrancasResult {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    // Query key inclui filtros e paginação para cache correto
    queryKey: ['financeiro-cobrancas-paginated', currentTenant?.id, filters, pagination],
    queryFn: async () => {
      logger.debug('Buscando cobranças paginadas:', { filters, pagination });

      // Chama RPC com todos os parâmetros
      // NOTA: Casting necessário até types.ts ser regenerado
      const { data, error } = await (supabase.rpc as any)('get_cobrancas_paginated', {
        p_page: pagination.page,
        p_page_size: pagination.pageSize,
        p_tenant_hint: currentTenant?.id || null,
        // Filtros
        p_status: filters?.status || null,
        // NOVO v1.6.0 (RPC v2.5.0): filtro por múltiplos status
        p_status_in: filters?.status_in && filters.status_in.length > 0 ? filters.status_in : null,
        p_tipo_cobranca: filters?.tipo_cobranca || null,
        p_meio_pagamento_id: filters?.meio_pagamento_id || null,
        p_product_id: filters?.product_id || null,
        p_search: filters?.search || null,
        // Filtros de data
        p_data_vencimento_de: filters?.data_vencimento_de || null,
        p_data_vencimento_ate: filters?.data_vencimento_ate || null,
        p_data_emissao_de: filters?.data_emissao_de || null,
        p_data_emissao_ate: filters?.data_emissao_ate || null,
        p_data_pagamento_de: filters?.data_pagamento_de || null,
        p_data_pagamento_ate: filters?.data_pagamento_ate || null,
        // NOVO v1.1.0: Filtro por categoria do produto
        p_category_id: filters?.category_id || null,
        // NOVO v1.3.0: Filtros para abas Financeiro (cliente/negócio)
        p_customer_id: filters?.customer_id || null,
        p_deal_id: filters?.deal_id || null,
        // NOVO: filtro por tarefa de origem (aba Financeiro do modal de tarefa)
        p_task_id: filters?.task_id || null,
        // NOVO v1.4.0: Filtro por provider_payment_link (modal link de pagamento)
        p_provider_payment_link: filters?.provider_payment_link || null,
        // NOVO v1.5.0 (RPC v1.9.0): filtros adicionais da listagem
        p_data_criacao_de: filters?.data_criacao_de || null,
        p_data_criacao_ate: filters?.data_criacao_ate || null,
        p_conta_corrente_id: filters?.conta_corrente_id || null,
        p_product_term_type: filters?.product_term_type || null,
        // NOVO: filtro por veículo (módulo Veículos)
        p_vehicle_id: filters?.vehicle_id || null,
      });

      if (error) {
        logger.error('Erro ao buscar cobranças paginadas:', error);
        throw error;
      }

      // Total count e KPIs vêm em cada row (pattern COUNT(*) OVER())
      const rawCobrancas = (data || []) as PaginatedCobranca[];
      const totalCount = rawCobrancas[0]?.total_count ?? 0;
      const kpis: CobrancaKpis = {
        totalFaturas: Number(rawCobrancas[0]?.kpi_total_faturas ?? 0),
        totalPagas: Number(rawCobrancas[0]?.kpi_total_pagas ?? 0),
        recebido: Number(rawCobrancas[0]?.kpi_recebido ?? 0),
        previsto: Number(rawCobrancas[0]?.kpi_previsto ?? 0),
      };

      logger.debug(`Recebidos ${rawCobrancas.length} cobranças, total: ${totalCount}`);

      return {
        cobrancas: rawCobrancas,
        totalCount: Number(totalCount),
        totalPages: Math.ceil(Number(totalCount) / pagination.pageSize) || 1,
        kpis,
      };
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    cobrancas: query.data?.cobrancas || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 1,
    kpis: query.data?.kpis || { totalFaturas: 0, totalPagas: 0, recebido: 0, previsto: 0 },
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => {
      queryClient.invalidateQueries({
        queryKey: ['financeiro-cobrancas-paginated', currentTenant?.id],
      });
    },
  };
}
